import { useState, useEffect } from 'react';
import { products } from '../../../mocks/products';

type Product = typeof products[0];

interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
  onAddToCart: (product: Product, variantIdx: number) => void;
}

export default function ProductModal({ product, onClose, onAddToCart }: ProductModalProps) {
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedSize, setSelectedSize] = useState('M');

  const isApparel = product?.category === 'HOODIES' || product?.category === 'TEES';
  const isWristband = product && 'sizeType' in product && (product as any).sizeType === 'wristband';
  const sizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL'];
  const wristbandSizes = ['Adult', 'Youth'];

  useEffect(() => {
    setSelectedVariantIdx(0);
    setQuantity(1);
    setAdded(false);
    setSelectedSize(isWristband ? 'Adult' : 'M');
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [product, onClose]);

  if (!product) return null;

  const activeImage =
    product.variants && product.variants.length > 0
      ? product.variants[selectedVariantIdx].image
      : product.image;

  const activeVariantLabel =
    product.variants && product.variants.length > 0
      ? product.variants[selectedVariantIdx].label
      : null;

  const handleAdd = () => {
    for (let i = 0; i < quantity; i++) {
      onAddToCart(product, selectedVariantIdx);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  const features = [
    { icon: 'ri-shield-check-line', text: 'Premium quality materials' },
    { icon: 'ri-truck-line', text: 'Free shipping on orders over $75' },
    { icon: 'ri-refresh-line', text: '30-day hassle-free returns' },
    { icon: 'ri-star-line', text: 'Official Nightmare merchandise' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="relative w-full max-w-4xl bg-[#0d0d0d] rounded-xl border border-gray-800 shadow-2xl overflow-hidden animate-modal-in"
        style={{ maxHeight: '90vh' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-[#1a1a1a] hover:bg-[#8B0000] text-gray-400 hover:text-white rounded-full transition-all duration-200 cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex flex-col md:flex-row overflow-y-auto" style={{ maxHeight: '90vh' }}>
          {/* Image Panel */}
          <div
            className="md:w-1/2 flex-shrink-0 flex items-center justify-center relative"
            style={{ background: '#6b7280', minHeight: '360px' }}
          >
            {product.badge && (
              <div className="absolute top-4 left-4 z-10">
                <span className="bg-[#8B0000] text-white text-xs font-bold uppercase px-3 py-1 rounded">
                  {product.badge}
                </span>
              </div>
            )}
            <img
              src={activeImage}
              alt={product.name}
              className="w-full h-full object-contain transition-all duration-300"
              style={{ maxHeight: '480px' }}
            />

            {/* Variant Thumbnails */}
            {product.variants && product.variants.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                {product.variants.map((variant, idx) => (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariantIdx(idx)}
                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer flex-shrink-0 ${
                      selectedVariantIdx === idx
                        ? 'border-white scale-110'
                        : 'border-gray-700 hover:border-gray-400 opacity-60 hover:opacity-100'
                    }`}
                    style={{ background: '#6b7280' }}
                    title={variant.label}
                  >
                    <img
                      src={variant.image}
                      alt={variant.label}
                      className="w-full h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Panel */}
          <div className="md:w-1/2 p-8 flex flex-col justify-between overflow-y-auto">
            <div>
              {/* Category Tag */}
              <span className="text-xs font-bold uppercase tracking-widest text-[#8B0000] mb-3 block">
                {product.category}
              </span>

              {/* Name */}
              <h2 className="text-white text-3xl font-bold mb-3 leading-tight">
                {product.name}
              </h2>

              {/* Price */}
              <p className="text-[#8B0000] text-3xl font-bold mb-6">
                $
                {typeof product.price === 'number' && product.price % 1 !== 0
                  ? product.price.toFixed(2)
                  : product.price}
              </p>

              {/* Active Variant Label */}
              {activeVariantLabel && (
                <p className="text-gray-400 text-sm mb-3">
                  Color: <span className="text-white font-semibold">{activeVariantLabel}</span>
                </p>
              )}

              {/* Color Swatches */}
              {product.variants && product.variants.length > 1 && (
                <div className="flex items-center gap-3 mb-6">
                  {product.variants.map((variant, idx) => (
                    <button
                      key={variant.id}
                      onClick={() => setSelectedVariantIdx(idx)}
                      className={`w-8 h-8 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${
                        selectedVariantIdx === idx
                          ? 'border-white scale-110'
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: variant.color }}
                      title={variant.label}
                    >
                      {selectedVariantIdx === idx && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Size Selector */}
              {isApparel && (
                <div className="mb-6">
                  <p className="text-gray-400 text-sm mb-2">Size</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[44px] h-10 px-3 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer border ${
                          selectedSize === size
                            ? 'bg-[#8B0000] border-[#8B0000] text-white'
                            : 'bg-[#1a1a1a] border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Wristband Size Selector */}
              {isWristband && (
                <div className="mb-6">
                  <p className="text-gray-400 text-sm mb-2">Size</p>
                  <div className="flex items-center gap-2">
                    {wristbandSizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[80px] h-10 px-4 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer border ${
                          selectedSize === size
                            ? 'bg-[#8B0000] border-[#8B0000] text-white'
                            : 'bg-[#1a1a1a] border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-500 text-xs mt-2">½&quot; silicone wristband</p>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-2">Quantity</p>
                <div className="flex items-center gap-0 w-fit border border-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center text-white font-semibold text-sm bg-[#111]" style={{ lineHeight: '40px' }}>
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-[#1a1a1a] transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2 mb-8">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-400">
                    <div className="w-5 h-5 flex items-center justify-center text-[#8B0000]">
                      <i className={f.icon}></i>
                    </div>
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleAdd}
                className={`w-full font-bold py-4 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-base ${
                  added
                    ? 'bg-green-700 text-white'
                    : 'bg-[#8B0000] hover:bg-[#6B0000] text-white'
                }`}
              >
                {added ? (
                  <>
                    <i className="ri-check-line mr-2"></i>ADDED TO CART!
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-bag-line mr-2"></i>ADD TO CART
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="w-full border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white font-semibold py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
              >
                CONTINUE SHOPPING
              </button>
            </div>
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @keyframes modalIn {
              from { opacity: 0; transform: scale(0.95) translateY(10px); }
              to { opacity: 1; transform: scale(1) translateY(0); }
            }
            .animate-modal-in {
              animation: modalIn 0.25s ease-out forwards;
            }
          `,
        }}
      />
    </div>
  );
}
